const brReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class BrHomeHeroSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.connectionId = (this.connectionId || 0) + 1;
    this.slides = [
      ...this.querySelectorAll(
        '.br-home-hero__slide'
      )
    ];

    this.dots = [
      ...this.querySelectorAll(
        '[data-hero-dot]'
      )
    ];

    if (!this.slides.length) {
      return;
    }

    this.prevButton =
      this.querySelector(
        '[data-hero-prev]'
      );

    this.nextButton =
      this.querySelector(
        '[data-hero-next]'
      );

    this.index = Math.max(
      0,
      this.slides.findIndex(
        (slide) =>
          slide.classList.contains(
            'is-active'
          )
      )
    );

    this.duration = Math.max(
      1000,
      Number(
        this.dataset.interval
      ) || 5000
    );

    this.elapsed = 0;
    this.progress = 0;
    this.startedAt = null;
    this.advanceTimer = null;
    this.preloadTimer = null;
    this.progressAnimation = null;
    this.changeRequest = 0;
    this.mediaPromises = new Map();

    this.pauseReasons =
      new Set();
    this.mobileMedia =
      window.matchMedia(
        '(max-width: 749px)'
      );

    this.swipePointerId = null;
    this.swipeStartX = 0;
    this.swipeStartY = 0;
    this.swipeDeltaX = 0;
    this.swipeDeltaY = 0;
    this.swipeHorizontal = false;

    this.suppressNextClick = false;
    this.swipeClickTimer = null;

    this.handlePointerDown =
      this.handlePointerDown.bind(
        this
      );

    this.handlePointerMove =
      this.handlePointerMove.bind(
        this
      );

    this.handlePointerUp =
      this.handlePointerUp.bind(
        this
      );

    this.handlePointerCancel =
      this.handlePointerCancel.bind(
        this
      );

    this.handleSwipeClick =
      this.handleSwipeClick.bind(
        this
      );

    this.handleVisibility =
      this.handleVisibility.bind(
        this
      );

    this.handleReducedMotion =
      this.handleReducedMotion.bind(
        this
      );

    this.handleBlockSelect =
      this.handleBlockSelect.bind(
        this
      );

    this.handleKeydown =
      this.handleKeydown.bind(
        this
      );

    this.handlePrevClick =
      () => {
        this.show(
          this.index - 1,
          true
        );
      };

    this.handleNextClick =
      () => {
        this.show(
          this.index + 1,
          true
        );
      };

    this.handleDotClick =
      (event) => {
        const nextIndex =
          Number(
            event.currentTarget
              .dataset.heroDot
          );

        if (
          !Number.isInteger(
            nextIndex
          ) ||
          nextIndex ===
            this.index
        ) {
          return;
        }

        this.show(
          nextIndex,
          true
        );
      };

    this.handleMouseEnter =
      () => {
        this.pause(
          'hover'
        );
      };

    this.handleMouseLeave =
      () => {
        this.resume(
          'hover'
        );
      };

    this.handleFocusIn =
      () => {
        this.pause(
          'focus'
        );
      };

    this.handleFocusOut =
      () => {
        window.requestAnimationFrame(
          () => {
            if (
              !this.contains(
                document.activeElement
              )
            ) {
              this.resume(
                'focus'
              );
            }
          }
        );
      };

    // Inactive media stays inert in its template until it is needed.
    // Slides keep their existing opacity cross-fade and accessibility state.
    this.slides.forEach((slide) => { slide.hidden = false; });

    this.renderSlides();
    this.renderDots();
    this.renderProgress();

    this.prevButton
      ?.addEventListener(
        'click',
        this.handlePrevClick
      );

    this.nextButton
      ?.addEventListener(
        'click',
        this.handleNextClick
      );

    this.dots.forEach(
      (dot) => {
        dot.addEventListener(
          'click',
          this.handleDotClick
        );
      }
    );

    this.addEventListener(
      'keydown',
      this.handleKeydown
    );

    this.addEventListener(
      'mouseenter',
      this.handleMouseEnter
    );

    this.addEventListener(
      'mouseleave',
      this.handleMouseLeave
    );

    this.addEventListener(
      'focusin',
      this.handleFocusIn
    );

    this.addEventListener(
      'focusout',
      this.handleFocusOut
    );
    this.addEventListener(
      'pointerdown',
      this.handlePointerDown
    );

    this.addEventListener(
      'pointermove',
      this.handlePointerMove
    );

    this.addEventListener(
      'pointerup',
      this.handlePointerUp
    );

    this.addEventListener(
      'pointercancel',
      this.handlePointerCancel
    );

    this.addEventListener(
      'click',
      this.handleSwipeClick,
      true
    );
    document.addEventListener(
      'visibilitychange',
      this.handleVisibility
    );

    if (
      brReducedMotion
        .addEventListener
    ) {
      brReducedMotion
        .addEventListener(
          'change',
          this.handleReducedMotion
        );
    }

    if (
      document.hidden
    ) {
      this.pauseReasons.add(
        'visibility'
      );
    }

    if (
      brReducedMotion.matches
    ) {
      this.pauseReasons.add(
        'motion'
      );
    }

    /*
     * Shopify recommends slideshows stay
     * paused in Theme Editor and show the
     * block selected by the merchant.
     */
    if (
      window.Shopify
        ?.designMode
    ) {
      this.pauseReasons.add(
        'editor'
      );

      document.addEventListener(
        'shopify:block:select',
        this.handleBlockSelect
      );
    }

    if ('IntersectionObserver' in window) {
      this.pauseReasons.add('offscreen');
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) this.resume('offscreen');
        else this.pause('offscreen');
      });
      this.visibilityObserver.observe(this);
    }

    // Give the initial image its full display interval after it has loaded.
    const connectionId = this.connectionId;
    this.pauseReasons.add('initial-image');
    this.prepareSlide(this.index, true).then(() => {
      if (this.isConnected && this.connectionId === connectionId) this.resume('initial-image');
    });
  }

  disconnectedCallback() {
    this.initialized = false;
    this.connectionId += 1;
    this.changeRequest += 1;
    this.visibilityObserver?.disconnect();
    this.stop();

    this.prevButton
      ?.removeEventListener(
        'click',
        this.handlePrevClick
      );

    this.nextButton
      ?.removeEventListener(
        'click',
        this.handleNextClick
      );

    this.dots?.forEach(
      (dot) => {
        dot.removeEventListener(
          'click',
          this.handleDotClick
        );
      }
    );

    this.removeEventListener(
      'keydown',
      this.handleKeydown
    );

    this.removeEventListener(
      'mouseenter',
      this.handleMouseEnter
    );

    this.removeEventListener(
      'mouseleave',
      this.handleMouseLeave
    );

    this.removeEventListener(
      'focusin',
      this.handleFocusIn
    );

    this.removeEventListener(
      'focusout',
      this.handleFocusOut
    );

    document.removeEventListener(
      'visibilitychange',
      this.handleVisibility
    );

    if (
      brReducedMotion
        .removeEventListener
    ) {
      brReducedMotion
        .removeEventListener(
          'change',
          this.handleReducedMotion
        );
    }

    document.removeEventListener(
      'shopify:block:select',
      this.handleBlockSelect
    );

    this.removeEventListener(
      'pointerdown',
      this.handlePointerDown
    );

    this.removeEventListener(
      'pointermove',
      this.handlePointerMove
    );

    this.removeEventListener(
      'pointerup',
      this.handlePointerUp
    );

    this.removeEventListener(
      'pointercancel',
      this.handlePointerCancel
    );

    this.removeEventListener(
      'click',
      this.handleSwipeClick,
      true
    );

    if (
      this.swipeClickTimer
    ) {
      window.clearTimeout(
        this.swipeClickTimer
      );

      this.swipeClickTimer = null;
    }
    
  }

  handleKeydown(event) {
    if (
      event.key ===
      'ArrowLeft'
    ) {
      event.preventDefault();

      this.show(
        this.index - 1,
        true
      );
    }

    if (
      event.key ===
      'ArrowRight'
    ) {
      event.preventDefault();

      this.show(
        this.index + 1,
        true
      );
    }
  }

  handleVisibility() {
    if (
      document.hidden
    ) {
      this.pause(
        'visibility'
      );
    } else {
      this.resume(
        'visibility'
      );
    }
  }

  handleReducedMotion(
    event
  ) {
    if (
      event.matches
    ) {
      this.pause(
        'motion'
      );

      this.resetProgress();
    } else {
      this.resume(
        'motion'
      );
    }
  }

  handleBlockSelect(
    event
  ) {
    const slide =
      event.target
        ?.closest?.(
          '.br-home-hero__slide'
        );

    if (
      !slide ||
      !this.contains(slide)
    ) {
      return;
    }

    const nextIndex =
      this.slides.indexOf(
        slide
      );

    if (
      nextIndex < 0
    ) {
      return;
    }

    this.show(
      nextIndex,
      true
    );
  }
  handlePointerDown(event) {
    if (
      !this.mobileMedia.matches ||
      this.slides.length < 2 ||
      !event.isPrimary ||
      event.pointerType ===
        'mouse' ||
      event.target.closest?.(
        '.br-home-hero__controls'
      )
    ) {
      return;
    }

    this.swipePointerId =
      event.pointerId;

    this.swipeStartX =
      event.clientX;

    this.swipeStartY =
      event.clientY;

    this.swipeDeltaX = 0;
    this.swipeDeltaY = 0;
    this.swipeHorizontal = false;

    this.pause(
      'swipe'
    );
  }


  handlePointerMove(event) {
    if (
      event.pointerId !==
      this.swipePointerId
    ) {
      return;
    }

    const deltaX =
      event.clientX -
      this.swipeStartX;

    const deltaY =
      event.clientY -
      this.swipeStartY;

    this.swipeDeltaX =
      deltaX;

    this.swipeDeltaY =
      deltaY;

    const absoluteX =
      Math.abs(
        deltaX
      );

    const absoluteY =
      Math.abs(
        deltaY
      );

    if (
      !this.swipeHorizontal
    ) {
      /*
      * Don't decide direction until
      * the finger has moved enough.
      */
      if (
        absoluteX < 12 &&
        absoluteY < 12
      ) {
        return;
      }

      /*
      * Vertical intent belongs to
      * normal page scrolling.
      */
      if (
        absoluteY >=
        absoluteX
      ) {
        this.resetSwipeState();

        this.resume(
          'swipe'
        );

        return;
      }

      this.swipeHorizontal =
        true;

      try {
        this.setPointerCapture(
          event.pointerId
        );
      } catch {
        /* Pointer capture is optional. */
      }
    }

    if (
      this.swipeHorizontal
    ) {
      /*
      * touch-action: pan-y keeps
      * normal vertical scrolling
      * available to the browser.
      */
      event.preventDefault();
    }
  }


  handlePointerUp(event) {
    if (
      event.pointerId !==
      this.swipePointerId
    ) {
      return;
    }

    const wasHorizontal =
      this.swipeHorizontal;

    const deltaX =
      this.swipeDeltaX;

    const shouldChange =
      wasHorizontal &&
      Math.abs(
        deltaX
      ) >= 48;

    const nextIndex =
      deltaX < 0
        ? this.index + 1
        : this.index - 1;

    try {
      if (
        this.hasPointerCapture(
          event.pointerId
        )
      ) {
        this.releasePointerCapture(
          event.pointerId
        );
      }
    } catch {
      /* No active pointer capture. */
    }

    this.resetSwipeState();

    /*
    * Any intentional horizontal
    * gesture must not activate the
    * CTA underneath the finger.
    */
    if (
      wasHorizontal
    ) {
      this.guardNextClick();
    }

    if (
      shouldChange
    ) {
      this.show(
        nextIndex,
        true
      );
    }

    this.resume(
      'swipe'
    );
  }


  handlePointerCancel(event) {
    if (
      event.pointerId !==
      this.swipePointerId
    ) {
      return;
    }

    try {
      if (
        this.hasPointerCapture(
          event.pointerId
        )
      ) {
        this.releasePointerCapture(
          event.pointerId
        );
      }
    } catch {
      /* No active pointer capture. */
    }

    this.resetSwipeState();

    this.resume(
      'swipe'
    );
  }


  resetSwipeState() {
    this.swipePointerId =
      null;

    this.swipeStartX = 0;
    this.swipeStartY = 0;
    this.swipeDeltaX = 0;
    this.swipeDeltaY = 0;

    this.swipeHorizontal =
      false;
  }


  guardNextClick() {
    this.suppressNextClick =
      true;

    if (
      this.swipeClickTimer
    ) {
      window.clearTimeout(
        this.swipeClickTimer
      );
    }

    this.swipeClickTimer =
      window.setTimeout(
        () => {
          this.suppressNextClick =
            false;

          this.swipeClickTimer =
            null;
        },
        350
      );
  }


  handleSwipeClick(event) {
    if (
      !this.suppressNextClick
    ) {
      return;
    }

    this.suppressNextClick =
      false;

    if (
      this.swipeClickTimer
    ) {
      window.clearTimeout(
        this.swipeClickTimer
      );

      this.swipeClickTimer =
        null;
    }

    event.preventDefault();
    event.stopPropagation();
  }
  canAutoplay() {
    return (
      this.dataset.autoplay ===
        'true' &&
      this.slides.length > 1
    );
  }

  prepareSlide(index, urgent = false) {
    const slide = this.slides[index];
    const template = slide?.querySelector('template[data-hero-media]');
    if (template) {
      const media = template.content.cloneNode(true);
      const image = media.querySelector('img');
      if (image) {
        image.loading = 'eager';
        image.fetchPriority = urgent ? 'high' : 'low';
      }
      template.replaceWith(media);
    }
    const image = slide?.querySelector('picture img');
    if (!image) return Promise.resolve(true);
    if (urgent && !image.complete) image.fetchPriority = 'high';
    if (image.complete && image.naturalWidth > 0) return Promise.resolve(true);
    if (this.mediaPromises.has(index)) return this.mediaPromises.get(index);

    const pending = new Promise((resolve) => {
      let timeout;
      const finish = () => {
        clearTimeout(timeout);
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        resolve(image.naturalWidth > 0);
      };
      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
      timeout = setTimeout(finish, 15000);
      if (image.complete) finish();
    }).then(async (loaded) => {
      if (loaded && image.decode) await image.decode().catch(() => {});
      if (!loaded) this.mediaPromises.delete(index);
      return loaded;
    });
    this.mediaPromises.set(index, pending);
    return pending;
  }

  async show(nextIndex, restart = false, automatic = false) {
    if (!this.slides.length) return;
    const index = (nextIndex + this.slides.length) % this.slides.length;
    const request = ++this.changeRequest;
    this.pause('loading');
    const ready = await this.prepareSlide(index, true);
    if (!this.isConnected || request !== this.changeRequest) return;
    this.pauseReasons.delete('loading');

    // A slow or broken image never replaces the current visible banner.
    // A background tab / offscreen carousel must not advance while loading.
    if (!ready || (automatic && this.pauseReasons.size > 0)) {
      if (!ready) this.resetProgress();
      this.start();
      return;
    }
    this.index = index;
    this.renderSlides();
    this.renderDots();
    if (restart) this.resetProgress();
    this.start();
  }

  renderSlides() {
    this.slides.forEach(
      (slide, index) => {
        const active =
          index ===
          this.index;

        slide.classList.toggle(
          'is-active',
          active
        );

        if (
          active
        ) {
          slide.removeAttribute(
            'aria-hidden'
          );

          slide.removeAttribute(
            'inert'
          );
        } else {
          slide.setAttribute(
            'aria-hidden',
            'true'
          );

          slide.setAttribute(
            'inert',
            ''
          );
        }
      }
    );
  }

  renderDots() {
    this.dots.forEach(
      (dot, index) => {
        const active =
          index ===
          this.index;

        dot.classList.toggle(
          'is-active',
          active
        );

        dot.setAttribute(
          'aria-pressed',
          String(active)
        );

        if (
          active
        ) {
          dot.setAttribute(
            'aria-current',
            'true'
          );
        } else {
          dot.removeAttribute(
            'aria-current'
          );
        }
      }
    );

    this.renderProgress();
  }

  renderProgress() {
    this.dots.forEach(
      (dot, index) => {
        const value =
          index === this.index
            ? this.progress
            : 0;

        dot.style.setProperty(
          '--br-hero-progress',
          String(value)
        );
      }
    );
  }

  start() {
    if (!this.isConnected || !this.canAutoplay() || this.pauseReasons.size > 0 || this.advanceTimer !== null) return;
    const remaining = Math.max(0, this.duration - this.elapsed);
    this.startedAt = performance.now() - this.elapsed;
    const progress = this.dots[this.index]?.querySelector('.br-home-hero__dot-progress');
    // Browser-managed transform animation avoids a DOM write every frame.
    if (progress?.animate) {
      this.progressAnimation = progress.animate(
        [{ transform: 'scaleX(' + this.progress + ')' }, { transform: 'scaleX(1)' }],
        { duration: remaining, easing: 'linear', fill: 'forwards' }
      );
    }
    this.preloadTimer = window.setTimeout(() => {
      this.preloadTimer = null;
      if (!this.pauseReasons.size) this.prepareSlide((this.index + 1) % this.slides.length);
    }, Math.max(0, remaining - 1500));
    this.advanceTimer = window.setTimeout(() => {
      this.advanceTimer = null;
      this.show(this.index + 1, true, true);
    }, remaining);
  }

  pause(reason) {
    if (
      this.pauseReasons.has(
        reason
      )
    ) {
      return;
    }

    this.captureProgress();

    this.pauseReasons.add(
      reason
    );
  }

  resume(reason) {
    this.pauseReasons.delete(
      reason
    );

    if (
      this.pauseReasons.size ===
      0
    ) {
      this.start();
    }
  }

  captureProgress() {
    if (
      this.startedAt !==
      null
    ) {
      this.elapsed =
        Math.min(
          this.duration,
          Math.max(
            0,
            performance.now() -
              this.startedAt
          )
        );

      this.progress =
        this.duration > 0
          ? this.elapsed /
            this.duration
          : 0;
    }

    this.startedAt = null;

    this.cancelFrame();
    this.renderProgress();
  }

  resetProgress() {
    this.elapsed = 0;
    this.progress = 0;
    this.startedAt = null;

    this.renderProgress();
  }

  cancelFrame() {
    window.clearTimeout(this.advanceTimer);
    window.clearTimeout(this.preloadTimer);
    this.advanceTimer = null;
    this.preloadTimer = null;
    this.progressAnimation?.cancel();
    this.progressAnimation = null;
  }

  stop() {
    this.cancelFrame();

    this.startedAt = null;
  }
}

class BrHomeCategorySlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-category-track]');
    this.prev = this.querySelector('[data-category-prev]');
    this.next = this.querySelector('[data-category-next]');
    this.prev?.addEventListener('click', () => this.move(-1));
    this.next?.addEventListener('click', () => this.move(1));
    this.track?.addEventListener('scroll', () => this.update(), { passive: true });
    this.update();
  }

  move(direction) {
    const card = this.track?.querySelector('.br-home-category__card');
    if (!card) return;
    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 14;
    this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: brReducedMotion.matches ? 'auto' : 'smooth' });
  }

  update() {
    if (!this.track) return;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd;
  }
}

class BrHomePrintSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.track = this.querySelector('[data-print-track]');
    this.prev = this.querySelector('[data-print-prev]');
    this.next = this.querySelector('[data-print-next]');

    if (!this.track) return;

    this.initialized = true;
    this.handlePrev = () => this.move(-1);
    this.handleNext = () => this.move(1);
    this.handleScroll = () => this.update();

    this.prev?.addEventListener('click', this.handlePrev);
    this.next?.addEventListener('click', this.handleNext);
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
    }

    this.update();
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.handlePrev);
    this.next?.removeEventListener('click', this.handleNext);
    this.track?.removeEventListener('scroll', this.handleScroll);
    this.resizeObserver?.disconnect();
    this.initialized = false;
  }

  move(direction) {
    const card = this.track?.querySelector('.br-home-print');
    if (!card) return;

    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 16;
    const distance = card.getBoundingClientRect().width + gap;

    this.track.scrollBy({
      left: direction * distance,
      behavior: brReducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  update() {
    if (!this.track) return;

    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;

    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd;
  }
}

class BrHomeGender extends HTMLElement {
  connectedCallback() {
    this.tabs = [
      ...this.querySelectorAll(
        '[role="tab"]'
      )
    ];

    this.panels = [
      ...this.querySelectorAll(
        '[role="tabpanel"]'
      )
    ];

    this.section =
      this.closest(
        '.br-home-gender'
      );

    this.switchControl =
      this.querySelector(
        '.br-home-gender__switch'
      );

    if (
      this.tabs.length < 2 ||
      !this.panels.length
    ) {
      return;
    }

    this.handleTabClick =
      (event) => {
        this.activate(
          event.currentTarget
        );
      };

    this.handleTabKeydown =
      (event) => {
        this.onKeydown(event);
      };

    this.handleSwitchClick =
      (event) => {
        /*
         * Clicking either actual tab is already
         * handled by handleTabClick.
         *
         * The empty center area visually contains
         * the WordPress-style switch, so clicking
         * that area toggles the selected gender too.
         */
        if (
          event.target.closest?.(
            '.br-home-gender__tab'
          )
        ) {
          return;
        }

        const currentIndex =
          this.tabs.findIndex(
            (tab) =>
              tab.getAttribute(
                'aria-selected'
              ) === 'true'
          );

        const nextIndex =
          currentIndex === 0
            ? 1
            : 0;

        this.activate(
          this.tabs[nextIndex]
        );

        this.tabs[nextIndex]
          .focus();
      };

    this.tabs.forEach(
      (tab) => {
        tab.addEventListener(
          'click',
          this.handleTabClick
        );

        tab.addEventListener(
          'keydown',
          this.handleTabKeydown
        );
      }
    );

    this.switchControl
      ?.addEventListener(
        'click',
        this.handleSwitchClick
      );

    const selectedTab =
      this.tabs.find(
        (tab) =>
          tab.getAttribute(
            'aria-selected'
          ) === 'true'
      ) ||
      this.tabs[0];

    this.activate(
      selectedTab
    );
  }

  disconnectedCallback() {
    this.tabs?.forEach(
      (tab) => {
        tab.removeEventListener(
          'click',
          this.handleTabClick
        );

        tab.removeEventListener(
          'keydown',
          this.handleTabKeydown
        );
      }
    );

    this.switchControl
      ?.removeEventListener(
        'click',
        this.handleSwitchClick
      );
  }

  onKeydown(event) {
    const allowedKeys = [
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End'
    ];

    if (
      !allowedKeys.includes(
        event.key
      )
    ) {
      return;
    }

    event.preventDefault();

    const current =
      this.tabs.indexOf(
        event.currentTarget
      );

    let next = current;

    if (
      event.key ===
      'ArrowRight'
    ) {
      next =
        (
          current + 1
        ) %
        this.tabs.length;
    }

    if (
      event.key ===
      'ArrowLeft'
    ) {
      next =
        (
          current -
          1 +
          this.tabs.length
        ) %
        this.tabs.length;
    }

    if (
      event.key ===
      'Home'
    ) {
      next = 0;
    }

    if (
      event.key ===
      'End'
    ) {
      next =
        this.tabs.length -
        1;
    }

    this.activate(
      this.tabs[next]
    );

    this.tabs[next]
      .focus();
  }

  activate(activeTab) {
    if (!activeTab) {
      return;
    }

    const activePanelId =
      activeTab.getAttribute(
        'aria-controls'
      );

    const activeGender =
      activeTab.dataset.gender ||
      'boy';

    this.tabs.forEach(
      (tab) => {
        const selected =
          tab === activeTab;

        tab.setAttribute(
          'aria-selected',
          String(selected)
        );

        tab.tabIndex =
          selected
            ? 0
            : -1;
      }
    );

    this.panels.forEach(
      (panel) => {
        panel.hidden =
          panel.id !==
          activePanelId;
      }
    );

    if (this.section) {
      this.section.dataset
        .activeGender =
        activeGender;
    }

    /*
    * Make the active homepage gender available
    * to independent sections further down the page.
    *
    * The dataset provides the initial state for
    * sections that connect after this component.
    */
    document.documentElement
      .dataset.brHomeGender =
      activeGender;

    document.dispatchEvent(
      new CustomEvent(
        'br:home-gender-change',
        {
          detail: {
            gender:
              activeGender
          }
        }
      )
    );
  }
}
class BrHomeFabric extends HTMLElement {
  connectedCallback() {
    this.section =
      this.querySelector(
        '.br-home-fabric'
      );

    this.figures = [
      ...this.querySelectorAll(
        '[data-fabric-gender]'
      )
    ];

    this.handleGenderChange =
      this.handleGenderChange.bind(
        this
      );

    document.addEventListener(
      'br:home-gender-change',
      this.handleGenderChange
    );

    this.activate(
      document.documentElement
        .dataset.brHomeGender ||
      this.section
        ?.dataset.activeGender ||
      'boy'
    );
  }

  disconnectedCallback() {
    document.removeEventListener(
      'br:home-gender-change',
      this.handleGenderChange
    );
  }

  handleGenderChange(event) {
    this.activate(
      event.detail?.gender
    );
  }

  activate(gender) {
    const activeGender =
      gender === 'girl'
        ? 'girl'
        : 'boy';

    if (this.section) {
      this.section.dataset
        .activeGender =
        activeGender;
    }

    this.figures.forEach(
      (figure) => {
        const active =
          figure.dataset
            .fabricGender ===
          activeGender;

        figure.hidden =
          !active;

        if (active) {
          figure.removeAttribute(
            'aria-hidden'
          );
        } else {
          figure.setAttribute(
            'aria-hidden',
            'true'
          );
        }
      }
    );
  }
}
class BrHomeGallery extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-gallery-card]')];
    this.index = 0;
    this.render();
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.move(-1, true));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.move(1, true));
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    this.handleVisibility = () => document.hidden ? this.stop() : this.start();
    document.addEventListener('visibilitychange', this.handleVisibility);
    this.inViewport = !('IntersectionObserver' in window);
    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.inViewport = entry.isIntersecting;
        if (this.inViewport) this.start();
        else this.stop();
      });
      this.visibilityObserver.observe(this);
    }
    this.handleMotion = () => brReducedMotion.matches ? this.stop() : this.start();
    brReducedMotion.addEventListener?.('change', this.handleMotion);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.visibilityObserver?.disconnect();
    brReducedMotion.removeEventListener?.('change', this.handleMotion);
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  move(direction, restart = false) {
    this.index = (this.index + direction + this.cards.length) % this.cards.length;
    this.render();
    if (restart) { this.stop(); this.start(); }
  }

  render() {
    const total = this.cards.length;
    this.cards.forEach((card, index) => {
      let offset = (index - this.index + total) % total;
      if (offset > Math.floor(total / 2)) offset -= total;
      card.dataset.position = String(Math.max(-2, Math.min(2, offset)));
      card.classList.toggle('is-active', offset === 0);
      card.setAttribute('aria-label', `Gallery image ${index + 1} of ${total}`);
      if (offset === 0) card.setAttribute('aria-current', 'true');
      else card.removeAttribute('aria-current');
      card.style.zIndex = String(10 - Math.abs(offset));
    });
  }

  start() {
    if (!this.inViewport || this.dataset.autoplay !== 'true' || this.cards.length < 2 || brReducedMotion.matches || document.hidden) return;
    this.stop();
    this.timer = window.setInterval(() => this.move(1), Number(this.dataset.interval) || 4000);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = undefined;
  }
}

if (!customElements.get('br-home-hero-slider')) {
  customElements.define(
    'br-home-hero-slider',
    BrHomeHeroSlider
  );
}

if (!customElements.get('br-home-category-slider')) {
  customElements.define(
    'br-home-category-slider',
    BrHomeCategorySlider
  );
}

if (!customElements.get('br-home-print-slider')) {
  customElements.define(
    'br-home-print-slider',
    BrHomePrintSlider
  );
}

if (!customElements.get('br-home-gender')) {
  customElements.define(
    'br-home-gender',
    BrHomeGender
  );
}

if (!customElements.get('br-home-fabric')) {
  customElements.define(
    'br-home-fabric',
    BrHomeFabric
  );
}

if (!customElements.get('br-home-gallery')) {
  customElements.define(
    'br-home-gallery',
    BrHomeGallery
  );
}
